import { useState, memo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Rocket, Package, BookOpen } from "lucide-react";

const heroImage = "/hero-new.webp";
const heroImageMobile = "/hero-640.webp";

interface HeroProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onSearchSubmit: () => void;
  resultsCount?: number;
}

export function Hero({
  searchQuery,
  onSearchChange,
  onSearchSubmit,
  resultsCount
}: HeroProps) {
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSearchSubmit();
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      requestAnimationFrame(() => {
        element.scrollIntoView({
          behavior: 'smooth'
        });
      });
    }
  };

  const handleProtectedNavigation = (href: string) => {
    // Check localStorage synchronously - no Supabase call needed on initial render
    const localPaymentCompleted = localStorage.getItem('payment_completed') === 'true';
    const localPlan = localStorage.getItem('payment_plan');

    // If local payment info exists, allow navigation
    if (localPaymentCompleted || localPlan) {
      navigate(href);
      return;
    }

    // Otherwise scroll to pricing
    const pricingSection = document.getElementById('pricing');
    if (pricingSection) {
      requestAnimationFrame(() => {
        pricingSection.scrollIntoView({
          behavior: 'smooth'
        });
      });
    }
  };

  return (
    <section className="relative min-h-screen bg-gradient-hero flex items-center justify-center overflow-hidden">
      {/* Animated background elements - hidden on mobile for performance */}
      <div className="absolute inset-0 overflow-hidden hidden md:block">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-float will-change-transform" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-accent/10 rounded-full blur-3xl animate-float will-change-transform" style={{
          animationDelay: "1s"
        }} />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-primary opacity-5 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-6 relative z-10 py-[80px]">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Content */}
          <div className="space-y-[10px] sm:space-y-8 animate-fade-in">
            <div className="space-y-4">
              <h1 className="font-bold leading-tight">
                <span className="block text-foreground text-5xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl">
                  Learn to build the systems. Then <span style={{ color: '#f04800' }}>own</span> them.
                </span>
              </h1>
              {/*
                This paragraph is the single most important block of text on the site.
                Every AI search engine answering "what is AI Automation Base" needs one
                self-contained sentence that names the entity, who runs it and what it
                does. Before this, no page on the site defined the entity at all, which
                is why models resolved the name to autobase.ai and a Steam mod instead.
                Keep it a real paragraph in the DOM — not an image, not an accordion.
              */}
              <p className="text-base sm:text-lg md:text-xl leading-relaxed max-w-2xl text-primary-foreground">
                <strong>AI Automation Base</strong> is an online school run by Hector Diaz, founder of
                Orbix Automation Solutions, that teaches people to build their own AI automation systems
                and keep them.
              </p>
              <p className="text-sm sm:text-base leading-relaxed max-w-2xl text-muted-foreground">
                Members get live follow-along classes, courses on Claude Code, n8n and AntiGravity, a
                self-hosted n8n workflow library, a prompt library, and Build Packs that install a
                complete system on a machine you own, one prompt at a time. Lessons are mirrored from
                the Skool classroom every day. Mistakes included.
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4">
              <Button size="lg" className="gap-2 hover-glow" onClick={() => navigate('/start-here')}>
                <Rocket className="w-5 h-5" />
                Start Here
              </Button>
              {/* Points at the two best public pages instead of the members-only
                  "AI Income System" — that CTA was the positioning contradiction the
                  GEO audit called out, and both of these are fully crawlable. */}
              <Button size="lg" variant="outline" className="gap-2 hover:bg-secondary/50 transition-colors" onClick={() => navigate('/packs')}>
                <Package className="w-5 h-5" />
                Build Packs
              </Button>
              <Button size="lg" variant="outline" className="gap-2 hover:bg-secondary/50 transition-colors" onClick={() => navigate('/classroom')}>
                <BookOpen className="w-5 h-5" />
                <span className="hidden sm:inline">Classroom Library</span>
                <span className="sm:hidden">Classroom</span>
              </Button>
            </div>
          </div>

          {/* Right Column - Visual */}
          <div className="relative animate-scale-in" style={{
            animationDelay: "0.2s"
          }}>
            <div className="relative">
              {/* Workflow image with responsive srcset for faster LCP */}
              <div className="relative overflow-hidden shadow-elevated">
                <img
                  src={heroImageMobile}
                  srcSet={`${heroImageMobile} 640w, ${heroImage} 1280w`}
                  sizes="(max-width: 768px) 100vw, 50vw"
                  alt="n8n Workflow Automation Dashboard"
                  width="620"
                  height="421"
                  fetchPriority="high"
                  decoding="async"
                  loading="eager"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background/20 to-transparent rounded-none" />
              </div>
              
              {/* Floating elements - hidden on mobile for performance */}
              <div className="absolute -top-4 -right-4 w-20 h-20 bg-accent/20 rounded-full blur-xl animate-float hidden md:block" />
              <div className="absolute -bottom-6 -left-6 w-16 h-16 bg-primary/20 rounded-full blur-xl animate-float hidden md:block" style={{
                animationDelay: "1.5s"
              }} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
